import React, { useState } from "react";
import { PlayArrow } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { selectVersionsByProjectId, useGetVersionConfigQuery } from "fond/api";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import CreateScenarioModal from "fond/project/versions/CreateScenarioModal";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner } from "fond/widgets";

import DataModificationPanel from "./DataModificationPanel";

interface AnalysisPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const AnalysisPanel: React.FC<AnalysisPanelProps> = ({ isReadOnly }) => {
  const projectId = useAppSelector((state) => state.project.projectId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, projectId));
  const { isLoading } = useGetVersionConfigQuery(versionId);
  const [openCreateScenarioModal, setOpenCreateScenarioModal] = useState<boolean>(false);

  const { workflowState } = useCustomWorkflow({ versionId, key: "nbn_cost_to_serve_iteration" });
  const isGeneratingCTS = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  if (isLoading) {
    return <BlockSpinner />;
  }

  return (
    <>
      {!isGeneratingCTS && (
        <>
          <Typography fontWeight="500">New Scenarios</Typography>
          <Box py={1}>
            <Typography variant="caption">
              Adjust your build scope by excluding specific service locations and see the updated cost to serve analysis.
            </Typography>
          </Box>
          <NavigationButton
            data-testid="input-data-navigation"
            icon={<PlayArrow />}
            title="Run new scenario"
            onClick={() => setOpenCreateScenarioModal(true)}
            sx={{ m: 0, mt: 1 }}
            disabled={isReadOnly || isGeneratingCTS}
          />
        </>
      )}

      <Box my={1}>
        <DataModificationPanel />
      </Box>

      {openCreateScenarioModal && (
        <CreateScenarioModal
          onClose={() => {
            setOpenCreateScenarioModal(false);
          }}
          versionNames={versions?.map((version) => version.Name)}
        />
      )}
    </>
  );
};

export default AnalysisPanel;
